import * as React from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import propz from 'propz';
import { AppUser } from '../../views/App/App';
import { Report } from '../../models/report';
import { SchoolEvent } from '../../models/event';
import { Point } from '../../models/venue';
import { SimpleModal } from '../SimpleModal/SimpleModal';
import { getEventAvailabilityWorkflow, requestEventAvailability, EventAvailabilityWorkflowState, AvailabilityWorkflowStatus } from '../../helpers/service/admin/messages';
import { getAvailabilitySummary, getAvailabilitySender } from '../../helpers/event/availabilitySummary';
import './SchoolEventAvailabilityReportsSummary.scss';

interface Props {
  reports: Report[];
  user: AppUser;
  schoolEvent?: SchoolEvent;
  onClickGotIt: (reportId: string) => void;
  onViewMapClick: (point: Point) => void;
}

type AvailabilityFilter = 'ALL' | AvailabilityWorkflowStatus | 'NEW';

interface ReportGroup {
  key: string;
  studentName: string;
  latest: Report;
  reports: Report[];
}

const getStudentName = (report: Report): string =>
  [
    propz.get(report, ['playerDetailsData', 'firstName'], ''),
    propz.get(report, ['playerDetailsData', 'lastName'], '')
  ].filter(Boolean).join(' ') || 'Student not specified';

const getReportGroupKey = (report: Report, index: number): string => {
  const userId =
    propz.get(report, ['playerDetails', 'userId'], '') ||
    propz.get(report, ['playerDetailsData', 'id'], '');
  const permissionId = propz.get(report, ['playerDetails', 'permissionId'], '');
  const normalizedName = getStudentName(report).trim().toLowerCase();

  return userId
    ? `user:${userId}`
    : permissionId
    ? `permission:${permissionId}`
    : normalizedName
    ? `student:${normalizedName}`
    : report.id || `report:${index}`;
};

const getCreatedAtValue = (report: Report): number => {
  const createdAt = moment(report.createdAt);
  return createdAt.isValid() ? createdAt.valueOf() : 0;
};

const getSender = getAvailabilitySender;

export const SchoolEventAvailabilityReportsSummary: React.FC<Props> = ({
  reports,
  user,
  schoolEvent,
  onClickGotIt
}) => {
  const [statusFilter, setStatusFilter] = React.useState<AvailabilityFilter>('ALL');
  const [studentSearch, setStudentSearch] = React.useState('');
  const [appliedStudentSearch, setAppliedStudentSearch] = React.useState('');
  const [selectedGroupKey, setSelectedGroupKey] = React.useState<string | null>(null);
  const [workflowState, setWorkflowState] = React.useState<EventAvailabilityWorkflowState | null>(null);
  const [workflowLoading, setWorkflowLoading] = React.useState(false);
  const [workflowError, setWorkflowError] = React.useState('');
  const [requestOpen, setRequestOpen] = React.useState(false);
  const [requestMode, setRequestMode] = React.useState<'REQUEST' | 'REMINDER'>('REQUEST');
  const [requestAudience, setRequestAudience] = React.useState<'PARENT' | 'STUDENT' | 'BOTH'>('BOTH');
  const [selectedPlayers, setSelectedPlayers] = React.useState<string[]>([]);
  const [reopenAnswered, setReopenAnswered] = React.useState(false);
  const [requestError, setRequestError] = React.useState('');
  const [requestMessage, setRequestMessage] = React.useState('');
  const [requestDeadline, setRequestDeadline] = React.useState('');
  const [requestStudentSearch, setRequestStudentSearch] = React.useState('');
  const [workflowNotice, setWorkflowNotice] = React.useState('');

  const loadWorkflow = React.useCallback(async () => {
    if (!schoolEvent || !schoolEvent.id) return;
    setWorkflowLoading(true);
    setWorkflowError('');
    try { setWorkflowState(await getEventAvailabilityWorkflow(user, schoolEvent.id)); }
    catch (error) {
      setWorkflowError(propz.get(
        error,
        ['response', 'data', 'details', 'text'],
        'Availability requests could not be loaded. Please try again.'
      ));
    }
    finally { setWorkflowLoading(false); }
  }, [schoolEvent && schoolEvent.id, user.roleSessionKey, user.activeSchoolId]);

  React.useEffect(() => { loadWorkflow(); }, [loadWorkflow]);

  const submitRequest = async (options: {pendingOnly?: boolean; force?: boolean} = {}) => {
    if (!schoolEvent || !workflowState) return;
    setWorkflowLoading(true); setRequestError(''); setWorkflowNotice('');
    try {
      const players = options.pendingOnly ? undefined : workflowState.items
        .filter(item => selectedPlayers.includes(String(item.player.permissionId)))
        .map(item => ({userId: String(item.player.userId), permissionId: String(item.player.permissionId)}));
      const isReminder = requestMode === 'REMINDER';
      const reopensExpired = workflowState.items.some(item => item.status === 'EXPIRED' && selectedPlayers.includes(String(item.player.permissionId)));
      const deadline = requestDeadline ? moment(requestDeadline).toISOString() : undefined;
      const next = await requestEventAvailability(user, schoolEvent.id, {players, recipients: requestAudience, message: requestMessage.trim(), deadline, force: isReminder || reopenAnswered || reopensExpired || options.force, ...options});
      const sentCount = Number((next as any).created || 0) + Number((next as any).reminded || 0) + Number((next as any).reopened || 0);
      setWorkflowState(next); setRequestOpen(false); setSelectedPlayers([]); setReopenAnswered(false); setRequestMessage(''); setRequestDeadline('');
      setWorkflowNotice(sentCount > 0
        ? `${isReminder ? 'Reminder' : 'Availability request'} sent for ${sentCount} ${sentCount === 1 ? 'student' : 'students'}.`
        : `No ${isReminder ? 'reminders' : 'requests'} were sent. The selected students may no longer be eligible.`);
    } catch (error) {
      setRequestError(propz.get(error, ['response', 'data', 'details', 'text'], 'The request could not be sent. Please try again.'));
    } finally { setWorkflowLoading(false); }
  };

  const groups = React.useMemo<ReportGroup[]>(() => {
    const reportsByStudent = reports.filter(report => typeof report.isTakePart === 'boolean').reduce((result, report, index) => {
      const key = getReportGroupKey(report, index);
      if (!result[key]) result[key] = [];
      result[key].push(report);
      return result;
    }, {} as Record<string, Report[]>);

    return Object.keys(reportsByStudent)
      .map(key => {
        const studentReports = [...reportsByStudent[key]].sort(
          (first, second) => getCreatedAtValue(second) - getCreatedAtValue(first)
        );
        return {
          key,
          studentName: getStudentName(studentReports[0]),
          latest: studentReports[0],
          reports: studentReports
        };
      })
      .sort((first, second) => first.studentName.localeCompare(second.studentName));
  }, [reports]);

  const normalizedSearch = appliedStudentSearch.trim().toLowerCase();
  const visibleGroups = groups.filter(group => {
    const matchesStatus =
      statusFilter === 'ALL' ||
      (statusFilter === 'AVAILABLE' ? group.latest.isTakePart : !group.latest.isTakePart);
    const matchesSearch = !normalizedSearch || group.studentName.toLowerCase().includes(normalizedSearch);
    return matchesStatus && matchesSearch;
  });

  const selectedGroup = selectedGroupKey ? groups.find(group => group.key === selectedGroupKey) : undefined;
  const availableCount = groups.filter(group => group.latest.isTakePart).length;
  const unavailableCount = groups.length - availableCount;
  const newCount = groups.filter(group => !group.latest.isActionPerformed).length;

  const renderDrawer = (): React.ReactNode => {
    if (!selectedGroup) return null;

    const { latest, studentName } = selectedGroup;
    const latestSender = getSender(latest);
    const latestCreatedAt = moment(latest.createdAt);

    const drawer = (
      <SimpleModal
        isOpen
        title={`${studentName} · Availability`}
        onCloseClick={() => setSelectedGroupKey(null)}
        onBackdropClick={() => setSelectedGroupKey(null)}
        showFooter={false}
        customClass="schoolAvailabilityDrawer"
        bodyClassName="schoolAvailabilityDrawer__body"
        side="right"
      >
        <div className="schoolAvailabilityDrawer__content">
          <section className="schoolAvailabilityDrawer__hero">
            <div>
              <span className="schoolAvailabilityDrawer__eyebrow">Latest response</span>
              <h2>{studentName}</h2>
              <p>
                Sent by {latestSender.fullName} · {latestSender.role.toLowerCase()}
                {latestCreatedAt.isValid() ? ` · ${latestCreatedAt.format('D MMM YYYY, HH:mm')}` : ''}
              </p>
            </div>
            <span className={`schoolAvailabilityStatus ${latest.isTakePart ? 'is-available' : 'is-unavailable'}`}>
              {latest.isTakePart ? 'Available' : 'Not available'}
            </span>
          </section>

          {!latest.isActionPerformed ? (
            <section className="schoolAvailabilityDrawer__newResponse">
              <div>
                <strong>New response</strong>
                <span>Mark this availability report as reviewed once it has been checked.</span>
              </div>
              <button type="button" onClick={() => onClickGotIt(latest.id)}>Mark as reviewed</button>
            </section>
          ) : null}

          <section className="schoolAvailabilityDrawer__section schoolAvailabilityDrawer__latestDetails">
            <div className="schoolAvailabilityDrawer__sectionHead">
              <h3>Latest details</h3>
              <span className={latest.isActionPerformed ? 'is-seen' : 'is-new'}>
                {latest.isActionPerformed ? 'Reviewed' : 'New'}
              </span>
            </div>
            <dl className="schoolAvailabilityDrawer__meta">
              <div><dt>Sent by</dt><dd>{latestSender.fullName}</dd></div>
              <div><dt>Role</dt><dd>{latestSender.role}</dd></div>
              <div>
                <dt>Sent at</dt>
                <dd>{latestCreatedAt.isValid() ? latestCreatedAt.format('D MMM YYYY, HH:mm') : '—'}</dd>
              </div>
              <div><dt>Availability</dt><dd>{latest.isTakePart ? 'Available' : 'Not available'}</dd></div>
            </dl>
            <div className="schoolAvailabilityDrawer__comment">
              <span>Comment</span>
              <p>{latest.details || 'No comment was provided.'}</p>
            </div>
          </section>

          <section className="schoolAvailabilityDrawer__section schoolAvailabilityDrawer__history">
            <div className="schoolAvailabilityDrawer__sectionHead">
              <h3>Response history</h3>
              <span>{selectedGroup.reports.length} {selectedGroup.reports.length === 1 ? 'response' : 'responses'}</span>
            </div>
            <ol>
              {selectedGroup.reports.map((report, index) => {
                const sender = getSender(report);
                const createdAt = moment(report.createdAt);
                return (
                  <li key={report.id || `${selectedGroup.key}:${index}`}>
                    <div className="schoolAvailabilityDrawer__historyTopline">
                      <span className={`schoolAvailabilityStatus ${report.isTakePart ? 'is-available' : 'is-unavailable'}`}>
                        {report.isTakePart ? 'Available' : 'Not available'}
                      </span>
                      {index === 0 ? <span className="schoolAvailabilityDrawer__latestBadge">Latest</span> : null}
                      <time dateTime={createdAt.isValid() ? createdAt.toISOString() : undefined}>
                        {createdAt.isValid() ? createdAt.format('D MMM YYYY, HH:mm') : 'Date unavailable'}
                      </time>
                    </div>
                    <div className="schoolAvailabilityDrawer__historySender">
                      <strong>{sender.fullName}</strong>
                      <span>{sender.role}</span>
                    </div>
                    <p>{report.details || 'No comment was provided.'}</p>
                  </li>
                );
              })}
            </ol>
          </section>
        </div>
      </SimpleModal>
    );

    return typeof document === 'undefined' ? drawer : createPortal(drawer, document.body);
  };

  const filters: Array<{ key: AvailabilityFilter; label: string; count: number }> = [
    { key: 'ALL', label: 'All', count: groups.length },
    { key: 'AVAILABLE', label: 'Available', count: availableCount },
    { key: 'NOT_AVAILABLE', label: 'Not available', count: unavailableCount }
  ];

  const statusLabel: Record<AvailabilityWorkflowStatus, string> = {
    AVAILABLE: 'Available', PENDING: 'Awaiting reply', EXPIRED: 'No reply by deadline', NOT_AVAILABLE: 'Not available',
    NOT_REQUESTED: workflowState?.workflow === 'SELF_SERVICE' ? 'No report yet' : 'Not requested', NO_RECIPIENT: 'No eligible recipient'
  };

  const requestItems = React.useMemo(() => {
    const query = requestStudentSearch.trim().toLowerCase();
    const eligibleItems = workflowState
      ? workflowState.items.filter(item => requestMode !== 'REMINDER' || item.status === 'PENDING' || item.status === 'EXPIRED')
      : [];
    if (!query) return eligibleItems;
    return eligibleItems.filter(item =>
      [item.player.firstName, item.player.lastName].filter(Boolean).join(' ').toLowerCase().includes(query)
    );
  }, [workflowState, requestMode, requestStudentSearch]);

  const renderRequestModal = () => {
    if (!requestOpen || !workflowState) return null;
    const modal = (
    <SimpleModal
      isOpen title={requestMode === 'REMINDER' ? 'Send availability reminder' : 'Request availability'} buttonText={requestMode === 'REMINDER' ? 'Send reminder' : 'Send request'}
      buttonDisabled={workflowLoading || selectedPlayers.length === 0}
      onButtonClick={() => submitRequest()} onCloseClick={() => setRequestOpen(false)}
      onBackdropClick={() => setRequestOpen(false)} customClass="availabilityRequestModal"
      dialogStyle={{maxWidth: 760}} zIndex={2200}
    >
      <div className="availabilityRequestModal__body">
        <p>{requestMode === 'REMINDER' ? 'Students who haven’t replied get another message. Their request and full history stay as they are.' : 'Ask families of students who weren’t included in the first request.'}</p>
        <div className="availabilityRequestModal__audience">
          {workflowState.parentEnabled ? <button type="button" className={requestAudience === 'PARENT' ? 'is-active' : ''} onClick={() => setRequestAudience('PARENT')}>Parents</button> : null}
          {workflowState.studentEnabled ? <button type="button" className={requestAudience === 'STUDENT' ? 'is-active' : ''} onClick={() => setRequestAudience('STUDENT')}>Students</button> : null}
          {workflowState.parentEnabled && workflowState.studentEnabled ? <button type="button" className={requestAudience === 'BOTH' ? 'is-active' : ''} onClick={() => setRequestAudience('BOTH')}>Parents and students</button> : null}
        </div>
        <label className="availabilityRequestModal__message">
          <span>Message to parents and students <small>Optional</small></span>
          <textarea
            value={requestMessage}
            maxLength={2000}
            placeholder="Add anything families need to know before responding — for example collection time, transport or kit."
            onChange={event => setRequestMessage(event.target.value)}
          />
          <small>{requestMessage.length}/2000</small>
        </label>
        <label className="availabilityRequestModal__deadline">
          <span>Response deadline <small>Optional</small></span>
          <input type="datetime-local" value={requestDeadline} max={schoolEvent && schoolEvent.startTime ? moment(schoolEvent.startTime).format('YYYY-MM-DDTHH:mm') : undefined} onChange={event => setRequestDeadline(event.target.value)} />
          <small>Leave empty to accept responses until the event starts.</small>
        </label>
        <label className="availabilityRequestModal__search">
          <span className="sr-only">Search students</span>
          <i aria-hidden="true" />
          <input type="search" value={requestStudentSearch} placeholder="Search students" onChange={event => setRequestStudentSearch(event.target.value)} />
          {requestStudentSearch ? <button type="button" aria-label="Clear search" onClick={() => setRequestStudentSearch('')}>×</button> : null}
        </label>
        <div className="availabilityRequestModal__selectAll"><button type="button" onClick={() => setSelectedPlayers(current => Array.from(new Set([...current, ...requestItems.map(item => String(item.player.permissionId))])))}>Select all{requestStudentSearch ? ' shown' : ''}</button><button type="button" onClick={() => setSelectedPlayers([])}>Clear selection</button><span>{selectedPlayers.length} {selectedPlayers.length === 1 ? 'student' : 'students'} · {workflowState.items.filter(item => selectedPlayers.includes(String(item.player.permissionId))).reduce((total, item) => total + (requestAudience === 'PARENT' ? item.eligibleRecipients.parent : requestAudience === 'STUDENT' ? item.eligibleRecipients.student : item.eligibleRecipients.total), 0)} recipients</span></div>
        <div className="availabilityRequestModal__people">
          {requestItems.map(item => <label key={String(item.player.permissionId)}><input type="checkbox" checked={selectedPlayers.includes(String(item.player.permissionId))} onChange={() => setSelectedPlayers(current => current.includes(String(item.player.permissionId)) ? current.filter(id => id !== String(item.player.permissionId)) : [...current, String(item.player.permissionId)])} /><span><strong>{[item.player.firstName, item.player.lastName].filter(Boolean).join(' ') || 'Student'}</strong><small>{statusLabel[item.status]}</small></span></label>)}
          {!requestItems.length ? <div className="availabilityRequestModal__empty">No students match your search.</div> : null}
        </div>
        {requestMode === 'REQUEST' ? <label className="availabilityRequestModal__reopen"><input type="checkbox" checked={reopenAnswered} onChange={event => setReopenAnswered(event.target.checked)} /><span><strong>Reopen existing responses</strong><small>Send a fresh request to selected students who have already answered. Their previous response remains in the history.</small></span></label> : null}
        {requestError ? <div className="availabilityRequestModal__error">{requestError}</div> : null}
      </div>
    </SimpleModal>
    );
    return typeof document === 'undefined' ? modal : createPortal(modal, document.body);
  };

  const workflowItems = workflowState ? [...workflowState.items] : [];
  const isSelfService = workflowState?.workflow === 'SELF_SERVICE';
  const workflowSummary = workflowState?.summary;
  const availabilitySummary = getAvailabilitySummary(workflowSummary || {}, isSelfService);
  const reachable = workflowItems.length - Number(workflowSummary?.NO_RECIPIENT || 0);
  const replied = Number(workflowSummary?.AVAILABLE || 0) + Number(workflowSummary?.NOT_AVAILABLE || 0);
  const noReply = Number(workflowSummary?.PENDING || 0) + Number(workflowSummary?.EXPIRED || 0);
  const workflowNewCount = workflowItems.filter(item => item.current && typeof item.current.isTakePart === 'boolean' && !item.current.isActionPerformed).length;
  const statusOrder: Record<AvailabilityWorkflowStatus, number> = {NOT_AVAILABLE:1,EXPIRED:2,PENDING:3,NOT_REQUESTED:4,AVAILABLE:5,NO_RECIPIENT:7};
  const query = studentSearch.trim().toLowerCase();
  const visibleWorkflowItems = workflowItems.filter(item => {
    const isNew = item.current && typeof item.current.isTakePart === 'boolean' && !item.current.isActionPerformed;
    return (statusFilter === 'ALL' || statusFilter === item.status || statusFilter === 'NEW' && isNew) &&
      (!query || [item.player.firstName,item.player.lastName].filter(Boolean).join(' ').toLowerCase().includes(query));
  }).sort((a,b) => {
    const aNew = a.current && typeof a.current.isTakePart === 'boolean' && !a.current.isActionPerformed;
    const bNew = b.current && typeof b.current.isTakePart === 'boolean' && !b.current.isActionPerformed;
    if (aNew !== bNew) return aNew ? -1 : 1;
    return (statusOrder[a.status] - statusOrder[b.status]) || [a.player.firstName,a.player.lastName].join(' ').localeCompare([b.player.firstName,b.player.lastName].join(' '));
  });
  const workflowFilters: Array<{key: AvailabilityFilter; label:string; count:number}> = [
    {key:'ALL',label:'All',count:workflowItems.length},
    {key:'AVAILABLE',label:'Available',count:Number(workflowSummary?.AVAILABLE||0)},
    {key:'NOT_AVAILABLE',label:'Not available',count:Number(workflowSummary?.NOT_AVAILABLE||0)},
    ...(isSelfService ? [
      {key:'NOT_REQUESTED' as AvailabilityFilter,label:'No report yet',count:Number(workflowSummary?.NOT_REQUESTED||0)}
    ] : [
      {key:'PENDING' as AvailabilityFilter,label:'Awaiting reply',count:Number(workflowSummary?.PENDING||0)},
      {key:'EXPIRED' as AvailabilityFilter,label:'No reply by deadline',count:Number(workflowSummary?.EXPIRED||0)},
      {key:'NOT_REQUESTED' as AvailabilityFilter,label:'Not requested',count:Number(workflowSummary?.NOT_REQUESTED||0)}
    ]),
    ...(Number(workflowSummary?.NO_RECIPIENT||0) ? [{key:'NO_RECIPIENT' as AvailabilityFilter,label:'No eligible recipient',count:Number(workflowSummary?.NO_RECIPIENT||0)}] : [])
  ];
  const openRequest = (mode:'REQUEST'|'REMINDER', permissionIds:string[]) => {
    setRequestMode(mode); setRequestDeadline(''); setSelectedPlayers(permissionIds); setRequestStudentSearch(''); setRequestOpen(true);
  };
  const openReportDrawer = (current:any) => {
    if (!current) return;
    const key = getReportGroupKey(current, 0);
    if (groups.some(group => group.key === key)) setSelectedGroupKey(key);
  };

  return (
    <section className="schoolAvailabilitySummary">
      {renderDrawer()}
      {renderRequestModal()}
      {workflowNotice ? <div className="availabilityWorkflowNotice" role="status"><span>✓</span><strong>{workflowNotice}</strong><button type="button" aria-label="Dismiss" onClick={() => setWorkflowNotice('')}>×</button></div> : null}
      {!workflowState ? workflowLoading ? <div className="availabilityWorkflowLoading">Loading availability…</div> : workflowError ? (
        <div className="availabilityWorkflowLoading" role="alert">
          <span>{workflowError}</span>
          <button type="button" onClick={loadWorkflow}>Try again</button>
        </div>
      ) : null : <>
      <header className="availabilityRedesign__header"><div><span className="availabilityRedesign__title"><h2>Availability</h2><em>{isSelfService ? 'Self-service' : 'School requests'}</em></span><p>{isSelfService ? 'Families report availability in the app whenever they like. No requests or reminders are sent for this event.' : <>Request availability from families{schoolEvent?.startTime ? <> · Replies close <b>{moment(schoolEvent.startTime).format('ddd D MMM, HH:mm')}</b> (event start)</> : null} unless a later request set its own deadline.</>}</p></div>{!isSelfService ? <div className="availabilityRedesign__actions"><button disabled={!workflowSummary?.NOT_REQUESTED} onClick={() => openRequest('REQUEST',workflowItems.filter(x=>x.status==='NOT_REQUESTED').map(x=>String(x.player.permissionId)))}>Request from {workflowSummary?.NOT_REQUESTED||0} not requested</button><button className="is-primary" disabled={!noReply} onClick={() => openRequest('REMINDER',workflowItems.filter(x=>x.status==='PENDING'||x.status==='EXPIRED').map(x=>String(x.player.permissionId)))}>Remind {noReply} without a reply</button></div> : null}</header>
      <div className="availabilityRedesign__overview"><div><strong className="availabilityRedesign__statusHeading">{availabilitySummary.heading}</strong><p>{availabilitySummary.detail}</p></div><section><div className="availabilityRedesign__bar"><i className="is-available" style={{flex:Number(workflowSummary?.AVAILABLE||0)}}/><i className="is-unavailable" style={{flex:Number(workflowSummary?.NOT_AVAILABLE||0)}}/>{!isSelfService?<><i className="is-pending" style={{flex:Number(workflowSummary?.PENDING||0)}}/><i className="is-expired" style={{flex:Number(workflowSummary?.EXPIRED||0)}}/><i className="is-not-requested" style={{flex:Number(workflowSummary?.NOT_REQUESTED||0)}}/></>:<i className="is-no-report" style={{flex:Math.max(0,reachable-replied)}}/>}</div><div className="availabilityRedesign__filters">{workflowFilters.map(filter=><button key={filter.key} className={statusFilter===filter.key?'is-active':''} onClick={()=>setStatusFilter(filter.key)}><i className={`is-${String(filter.key).toLowerCase().replace('_','-')}`}/>{filter.label} <span>{filter.count}</span></button>)}</div></section></div>
      {workflowNewCount ? <aside className="availabilityRedesign__banner is-info"><b>{workflowNewCount}</b><p><strong>{workflowNewCount} new {workflowNewCount===1?'report':'reports'}</strong> since you last checked.</p><button onClick={()=>setStatusFilter('NEW')}>Show</button></aside>:null}
      {workflowSummary?.NO_RECIPIENT ? <aside className="availabilityRedesign__banner is-warning"><b>!</b><p><strong>{workflowSummary.NO_RECIPIENT} students can’t {isSelfService?'report':'be asked'}</strong> — no linked parent and no student account.</p><button onClick={()=>setStatusFilter('NO_RECIPIENT')}>Show students</button></aside>:null}
      <div className="availabilityRedesign__toolbar"><button className={statusFilter==='NEW'?'is-active':''} onClick={()=>setStatusFilter(statusFilter==='NEW'?'ALL':'NEW')}>● New reports <span>{workflowNewCount}</span></button><label><span className="schoolAvailabilitySummary__searchIcon"/><input type="search" value={studentSearch} placeholder="Search students" onChange={event=>setStudentSearch(event.target.value)}/>{studentSearch?<button onClick={()=>setStudentSearch('')}>×</button>:null}</label></div>
      <div className="schoolAvailabilitySummary__tableWrap"><table className="schoolAvailabilitySummary__table availabilityRedesign__table">
          <thead>
            <tr><th>Student</th><th>Availability</th><th>Comment</th><th>{isSelfService?'Reported by':'Reported by / request'}</th><th>Review</th><th/></tr>
          </thead>
          <tbody>{visibleWorkflowItems.length?visibleWorkflowItems.map(item=>{const current=item.current;const sender=current?getSender(current,item.player):null;const isNew=current&&typeof current.isTakePart==='boolean'&&!current.isActionPerformed;const name=[item.player.firstName,item.player.lastName].filter(Boolean).join(' ')||'Student';return <tr key={String(item.player.permissionId)} className={`${isNew?'is-new ':''}${item.status==='NO_RECIPIENT'?'is-no-recipient':''}`} onClick={()=>openReportDrawer(current)}><td><strong>{name}</strong></td><td><span className={`schoolAvailabilityStatus is-${item.status.toLowerCase().replace('_','-')}`}>{statusLabel[item.status]}</span></td><td><span className="schoolAvailabilitySummary__comment">{current&&typeof current.isTakePart==='boolean'?(current.details||'No comment'):'—'}</span></td><td>{current&&typeof current.isTakePart==='boolean'?<><strong>{sender?.fullName}</strong><small><em>{sender?.role.toUpperCase()}</em> · {moment(current.createdAt).format('D MMM, HH:mm')}{item.history.length>1?` · Changed ${item.history.length-1}×`:''}</small></>:item.status==='PENDING'?<><strong>Requested {current?.createdAt?moment(current.createdAt).format('D MMM'):'—'}</strong><small>No reminder yet</small></>:item.status==='EXPIRED'?<><strong className="is-overdue">No reply by deadline</strong><small>{current?.deadlineForAnswers?moment(current.deadlineForAnswers).format('D MMM, HH:mm'):''}</small></>:item.status==='NOT_REQUESTED'?(isSelfService?'No report yet':'Not requested yet'):item.status==='NO_RECIPIENT'?'No parent linked, no student account':'—'}</td><td>{isNew?<span className="schoolAvailabilityReview is-new">New</span>:current&&typeof current.isTakePart==='boolean'?<span className="schoolAvailabilityReview is-seen">Reviewed</span>:null}</td><td>{isNew?<button onClick={event=>{event.stopPropagation();onClickGotIt(current.id);}}>Mark reviewed</button>:item.status==='PENDING'||item.status==='EXPIRED'?<button onClick={event=>{event.stopPropagation();openRequest('REMINDER',[String(item.player.permissionId)]);}}>Remind</button>:item.status==='NOT_REQUESTED'&&!isSelfService?<button onClick={event=>{event.stopPropagation();openRequest('REQUEST',[String(item.player.permissionId)]);}}>Request</button>:current?'›':''}</td></tr>;}):<tr><td colSpan={6} className="schoolAvailabilitySummary__empty"><strong>No students match</strong><span>Try a different filter or clear the search.</span></td></tr>}</tbody>
        </table></div><footer className="availabilityRedesign__footer"><span>{visibleWorkflowItems.length} of {workflowItems.length} students</span><span>Sorted by: <strong>new reports, then needs attention</strong></span></footer>
      </>}
    </section>
  );
};

SchoolEventAvailabilityReportsSummary.displayName = 'SchoolEventAvailabilityReportsSummary';
